import { createFileRoute } from '@tanstack/react-router';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getApiClient } from '@apps/web/lib/api_client';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminDlqMessages,
    useAdminDlqStreams,
    useAdminMessageQueueStatus,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/admin/message-queue')({
    head: () => ({
        meta: [
            {
                title: 'Message Queue | Phoebe Admin',
            },
        ],
    }),
    component: MessageQueuePage,
});

function MessageQueuePage() {
    const [queueModalOpen, setQueueModalOpen] = useState(false);
    const [reconcileModalOpen, setReconcileModalOpen] = useState(false);

    return (
        <div className="flex h-screen flex-col p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold">Message Queue</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    Monitor the Redis Streams message queue for async task
                    processing.
                </p>
            </div>
            <div className="mb-4 flex gap-2">
                <Button onClick={() => setQueueModalOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Queue Shift Confirmations
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setReconcileModalOpen(true)}
                >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reconcile Search Index
                </Button>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-6 overflow-hidden xl:grid-cols-2">
                <MessageQueueList />
                <DlqPanel />
            </div>
            <QueueShiftConfirmationsDialog
                open={queueModalOpen}
                onOpenChange={setQueueModalOpen}
            />
            <ReconcileSearchIndexDialog
                open={reconcileModalOpen}
                onOpenChange={setReconcileModalOpen}
            />
        </div>
    );
}

function QueueShiftConfirmationsDialog({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const [selectedOrgIds, setSelectedOrgIds] = useState<Array<string>>([]);
    const [allOrgs, setAllOrgs] = useState(false);
    const [isQueuing, setIsQueuing] = useState(false);
    const handleQueueShiftConfirmations = async () => {
        if (!allOrgs && selectedOrgIds.length === 0) {
            return;
        }
        try {
            setIsQueuing(true);
            await getApiClient().post({
                url: '/admin/message-queue/queue/shift-confirmations',
                body: {
                    all_organizations: allOrgs,
                    organization_id: allOrgs ? null : selectedOrgIds[0],
                },
            });
            onOpenChange(false);
            setSelectedOrgIds([]);
            setAllOrgs(false);
        } finally {
            setIsQueuing(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>Queue Shift Confirmations</DialogTitle>
                    <DialogDescription>
                        Queue shift confirmations based on today’s scheduled
                        rules.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="all-orgs"
                            checked={allOrgs}
                            onCheckedChange={(checked) => {
                                setAllOrgs(checked === true);
                                if (checked) {
                                    setSelectedOrgIds([]);
                                }
                            }}
                        />
                        <Label htmlFor="all-orgs" className="cursor-pointer">
                            All organizations
                        </Label>
                    </div>
                    {allOrgs ? (
                        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950/50 dark:text-yellow-300">
                            <strong>Warning:</strong> This will queue shift
                            confirmations for all organizations with shift
                            confirmation settings enabled. Only use this if you
                            intend to trigger a global send.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Label>Organization</Label>
                            <OrganizationCombobox
                                value={selectedOrgIds}
                                onValueChange={setSelectedOrgIds}
                                placeholder="Select an organization..."
                            />
                        </div>
                    )}
                    <p className="text-muted-foreground text-sm">
                        This will send shift confirmations for shifts based on
                        today’s scheduled rules.
                    </p>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                            setSelectedOrgIds([]);
                            setAllOrgs(false);
                        }}
                        disabled={isQueuing}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleQueueShiftConfirmations}
                        disabled={
                            isQueuing ||
                            (!allOrgs && selectedOrgIds.length === 0)
                        }
                    >
                        {isQueuing ? 'Queuing...' : 'Send Shift Confirmations'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReconcileSearchIndexDialog({
    open,
    onOpenChange,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
}) {
    const [selectedOrgIds, setSelectedOrgIds] = useState<Array<string>>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        const orgId = selectedOrgIds[0];
        if (!orgId) {
            return;
        }
        try {
            setIsSubmitting(true);
            await getApiClient().post({
                url: '/admin/message-queue/queue/reconcile-search-index',
                body: {
                    organization_id: orgId,
                },
            });
            onOpenChange(false);
            setSelectedOrgIds([]);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>Reset Reconcile Cursors</DialogTitle>
                    <DialogDescription>
                        Reset the reconcile cursors for an organization. The
                        background reconciler will restart from the current
                        incremental cursor and re-verify all older records.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>Organization</Label>
                        <OrganizationCombobox
                            value={selectedOrgIds}
                            onValueChange={setSelectedOrgIds}
                            placeholder="Select an organization"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                            setSelectedOrgIds([]);
                        }}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || selectedOrgIds.length === 0}
                    >
                        {isSubmitting ? 'Resetting' : 'Reset Cursors'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MessageQueueList() {
    const { data, isLoading, mutate } = useAdminMessageQueueStatus();
    const { mutate: mutateDlq } = useAdminDlqStreams();
    const [confirmDeleteType, setConfirmDeleteType] = useState<string | null>(
        null,
    );
    const [isDeleting, setIsDeleting] = useState(false);

    const streams = data?.streams ?? [];
    const totalPending = data?.total_pending ?? 0;

    const performDelete = async () => {
        if (!confirmDeleteType) {
            return;
        }
        try {
            setIsDeleting(true);
            await getApiClient().post({
                url: '/admin/message-queue/queue/delete',
                body: { message_type: confirmDeleteType },
            });
            setConfirmDeleteType(null);
            await Promise.all([mutate(), mutateDlq()]);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <React.Fragment>
            <div className="border-border flex flex-col overflow-hidden rounded-md border">
                <div className="border-border border-b p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold">
                                Queue Status
                            </h2>
                            {totalPending > 0 && (
                                <p className="text-muted-foreground text-sm">
                                    {totalPending} pending{' '}
                                    {totalPending === 1
                                        ? 'message'
                                        : 'messages'}
                                </p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => mutate()}
                            disabled={isLoading}
                        >
                            <RefreshCw
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    isLoading ? 'animate-spin' : '',
                                )}
                            />
                            Refresh
                        </Button>
                    </div>
                    <div className="text-muted-foreground mt-2 text-xs">
                        Requeue only processes the messages currently loaded in
                        the table. Clear permanently deletes all messages in the
                        selected DLQ stream.
                    </div>
                </div>
                <div className="min-h-0 flex-1 overflow-auto">
                    {isLoading && streams.length === 0 ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                                <Spinner />
                                <span>Loading queue status…</span>
                            </div>
                        </div>
                    ) : !isLoading && streams.length === 0 ? (
                        <div className="flex h-32 items-center justify-center">
                            <p className="text-muted-foreground">
                                No active queue streams found.
                            </p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Message Type</TableHead>
                                    <TableHead>Total</TableHead>
                                    <TableHead>Pending</TableHead>
                                    <TableHead>Last ID</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {streams.map((stream) => (
                                    <TableRow key={stream.stream_name}>
                                        <TableCell className="font-mono text-sm">
                                            {stream.message_type}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary">
                                                {stream.length}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {stream.pending_count > 0 ? (
                                                <Badge variant="destructive">
                                                    {stream.pending_count}
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline">
                                                    0
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {stream.last_generated_id ?? 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    setConfirmDeleteType(
                                                        stream.message_type,
                                                    )
                                                }
                                            >
                                                <Trash2 className="text-destructive h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
            <Dialog
                open={confirmDeleteType !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setConfirmDeleteType(null);
                    }
                }}
            >
                <DialogContent className="sm:max-w-[560px]">
                    <DialogHeader>
                        <DialogTitle>Delete Queue</DialogTitle>
                        <DialogDescription>
                            This will delete the queue stream and its DLQ for
                            {' \u2018'}
                            {confirmDeleteType}
                            {'\u2019'}. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirmDeleteType(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={performDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Deleting' : 'Confirm Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}

function DlqPanel() {
    const { data: streams, isLoading, mutate } = useAdminDlqStreams();
    const [selectedStreamName, setSelectedStreamName] = useState<string | null>(
        null,
    );
    const [confirmClearOpen, setConfirmClearOpen] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [requeueRecentOpen, setRequeueRecentOpen] = useState(false);
    const {
        data: messages,
        isLoading: loadingMessages,
        mutate: mutateMessages,
    } = useAdminDlqMessages(selectedStreamName, 50);
    const onRequeueAll = async () => {
        if (!selectedStreamName || !messages || messages.length === 0) {
            return;
        }
        await getApiClient().post({
            url: '/admin/message-queue/dlq/requeue',
            body: {
                message_type: selectedStreamName,
                message_ids: messages.map((message) => message.message_id),
                reset_attempts: true,
            },
        });
        await Promise.all([mutateMessages(), mutate()]);
    };

    const selectedStream = useMemo(
        () =>
            (streams ?? []).find(
                (stream) => stream.message_type === selectedStreamName,
            ) || null,
        [streams, selectedStreamName],
    );

    const performClearAll = async () => {
        if (!selectedStreamName) {
            return;
        }
        try {
            setIsClearing(true);
            await getApiClient().post({
                url: '/admin/message-queue/dlq/clear',
                body: {
                    message_type: selectedStreamName,
                },
            });
            setConfirmClearOpen(false);
            await Promise.all([mutateMessages(), mutate()]);
        } finally {
            setIsClearing(false);
        }
    };

    return (
        <React.Fragment>
            <div className="border-border flex flex-col overflow-hidden rounded-md border">
                <div className="border-border border-b p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold">
                                Dead Letter Queue
                            </h2>
                            <p className="text-muted-foreground text-sm">
                                Inspect and requeue failed messages.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    mutate();
                                    if (selectedStreamName) {
                                        mutateMessages();
                                    }
                                }}
                                disabled={isLoading || loadingMessages}
                            >
                                <RefreshCw
                                    className={cn(
                                        'mr-2 h-4 w-4',
                                        isLoading || loadingMessages
                                            ? 'animate-spin'
                                            : '',
                                    )}
                                />
                                Refresh
                            </Button>
                            <Button
                                size="sm"
                                onClick={onRequeueAll}
                                disabled={
                                    !selectedStreamName ||
                                    !messages ||
                                    messages.length === 0
                                }
                            >
                                {selectedStreamName && messages
                                    ? `Requeue Shown (${messages.length})`
                                    : 'Requeue Shown'}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRequeueRecentOpen(true)}
                                disabled={!selectedStreamName}
                            >
                                Requeue Recent…
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmClearOpen(true)}
                                disabled={!selectedStreamName}
                            >
                                Clear Entire Stream
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-2">
                    <div className="flex min-h-0 flex-col space-y-2 overflow-hidden">
                        <h3 className="text-muted-foreground flex-shrink-0 text-sm font-medium">
                            DLQ Streams
                        </h3>
                        <div className="min-h-0 flex-1 overflow-auto rounded border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Message Type</TableHead>
                                        <TableHead>Messages</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(streams ?? []).map((stream) => (
                                        <TableRow
                                            key={stream.stream_name}
                                            className={cn(
                                                'cursor-pointer',
                                                selectedStreamName ===
                                                    stream.message_type
                                                    ? 'bg-muted/50'
                                                    : '',
                                            )}
                                            onClick={() =>
                                                setSelectedStreamName(
                                                    stream.message_type,
                                                )
                                            }
                                        >
                                            <TableCell className="font-mono text-sm">
                                                {stream.message_type}
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        stream.length > 0
                                                            ? 'destructive'
                                                            : 'secondary'
                                                    }
                                                >
                                                    {stream.length}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                    <div className="flex min-h-0 flex-col space-y-2 overflow-hidden">
                        <h3 className="text-muted-foreground flex-shrink-0 text-sm font-medium">
                            Messages{' '}
                            {selectedStreamName
                                ? `(${selectedStreamName})`
                                : ''}
                        </h3>
                        <div className="min-h-0 flex-1 overflow-auto rounded border">
                            {loadingMessages ? (
                                <div className="flex h-32 items-center justify-center">
                                    <div className="text-muted-foreground flex items-center gap-2">
                                        <Spinner />
                                        <span>Loading messages…</span>
                                    </div>
                                </div>
                            ) : !messages || messages.length === 0 ? (
                                <div className="flex h-32 items-center justify-center">
                                    <p className="text-muted-foreground">
                                        No messages.
                                    </p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>ID</TableHead>
                                            <TableHead>Attempt</TableHead>
                                            <TableHead>Error</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {messages.map((message) => (
                                            <TableRow key={message.message_id}>
                                                <TableCell className="font-mono text-xs">
                                                    {message.message_id}
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">
                                                    {message.attempt ?? '—'} /{' '}
                                                    {message.max_attempts ??
                                                        '—'}
                                                </TableCell>
                                                <TableCell className="max-w-[320px] truncate text-xs">
                                                    {message.error_type ? (
                                                        <span className="font-mono">
                                                            {message.error_type}
                                                            : {message.error}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground">
                                                            —
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Dialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
                <DialogContent className="sm:max-w-[560px]">
                    <DialogHeader>
                        <DialogTitle>Clear DLQ Stream</DialogTitle>
                        <DialogDescription>
                            {selectedStreamName ? (
                                <span>
                                    This action permanently deletes all messages
                                    in the DLQ stream ’{selectedStreamName}’.
                                    Ensure these messages are safe to discard
                                    and do not contain data that needs to be
                                    processed. Clearing is appropriate for
                                    disposable items such as idempotent
                                    scheduled jobs. This cannot be undone.
                                </span>
                            ) : null}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-muted-foreground text-sm">
                        {selectedStream ? (
                            <p>
                                Stream contains{' '}
                                <span className="font-mono font-medium">
                                    {selectedStream.length}
                                </span>{' '}
                                message{selectedStream.length === 1 ? '' : 's'}.
                            </p>
                        ) : null}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirmClearOpen(false)}
                            disabled={isClearing}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={performClearAll}
                            disabled={isClearing}
                        >
                            {isClearing ? 'Clearing…' : 'Confirm Clear'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <RequeueRecentDialog
                open={requeueRecentOpen}
                onOpenChange={setRequeueRecentOpen}
                messageType={selectedStreamName}
                onRequeued={async () => {
                    await Promise.all([mutateMessages(), mutate()]);
                }}
            />
        </React.Fragment>
    );
}

function RequeueRecentDialog({
    open,
    onOpenChange,
    messageType,
    onRequeued,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly messageType: string | null;
    readonly onRequeued: () => Promise<void>;
}) {
    const [hours, setHours] = useState('24');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState<{
        requeued_count: number;
        skipped_count: number;
    } | null>(null);

    const parsedHours = Number(hours);
    const hoursValid =
        Number.isFinite(parsedHours) && parsedHours > 0 && parsedHours <= 168;

    const handleSubmit = async () => {
        if (!messageType || !hoursValid) {
            return;
        }
        try {
            setIsSubmitting(true);
            setResult(null);
            const response = await getApiClient().post({
                url: '/admin/message-queue/dlq/requeue-recent',
                body: {
                    message_type: messageType,
                    hours: parsedHours,
                    reset_attempts: true,
                },
            });
            setResult({
                requeued_count: response.requeued_count,
                skipped_count: response.skipped_count,
            });
            await onRequeued();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setResult(null);
        onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    handleClose();
                    return;
                }
                onOpenChange(next);
            }}
        >
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>Requeue Recent Messages</DialogTitle>
                    <DialogDescription>
                        Requeue every dead-lettered {'\u2018'}
                        {messageType}
                        {'\u2019'} message enqueued in the last N hours back
                        onto the main stream. Attempts are reset for a clean
                        retry.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="requeue-hours">Lookback (hours)</Label>
                        <Input
                            id="requeue-hours"
                            type="number"
                            min={0}
                            max={168}
                            step="any"
                            value={hours}
                            onChange={(event) => setHours(event.target.value)}
                        />
                        <p className="text-muted-foreground text-xs">
                            Between 0 and 168 (7 days).
                        </p>
                    </div>
                    {result ? (
                        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                            Requeued {result.requeued_count} message
                            {result.requeued_count === 1 ? '' : 's'}
                            {result.skipped_count > 0
                                ? ` (skipped ${result.skipped_count} without a payload)`
                                : ''}
                            .
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        {result ? 'Close' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !messageType || !hoursValid}
                    >
                        {isSubmitting ? 'Requeuing…' : 'Requeue'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
